import React, { useState } from 'react';
import AIDrillButton from './AIDrillButton';

const GrammarUnit1Content = ({ unitData, userPreferences, onMarkComplete, userProgress }) => {
  const [currentSection, setCurrentSection] = useState('overview');
  const [currentQuiz, setCurrentQuiz] = useState(0);
  const [showAnswers, setShowAnswers] = useState(false);

  // Safety check for unit data
  if (!unitData || !unitData.content) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-800/30 rounded-xl p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">No Content Available</h2>
            <p className="text-gray-300">Content for Unit 1 is not available.</p>
          </div>
        </div>
      </div>
    );
  }

  const { content } = unitData;

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">英文五大句型概覽</h3>
        <p className="text-gray-300 mb-6">{content.overview.description}</p>
        
        <div className="grid gap-4">
          {content.overview.patterns.map((pattern, index) => (
            <div key={index} className="bg-gray-700/50 rounded-lg p-4">
              <h4 className="text-lg font-semibold mb-2 text-green-300">
                {pattern.id}. {pattern.name}
              </h4>
              <div className="space-y-2">
                <p className="text-yellow-300 font-mono">{pattern.structure}</p>
                <p className="text-gray-300">{pattern.description}</p>
                <p className="text-gray-400">{pattern.chinese}</p>
                <p className="text-blue-300 font-semibold">{pattern.example}</p>
                <p className="text-gray-300">{pattern.explanation}</p>
                
                <div className="mt-3">
                  <h5 className="text-sm font-semibold text-purple-300 mb-2">組成要素：</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {Object.entries(pattern.components).map(([key, value]) => (
                      <div key={key} className="text-sm">
                        <span className="text-yellow-300 font-mono">{key}:</span>
                        <span className="text-gray-300 ml-2">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {pattern.examples && (
                  <div className="mt-3">
                    <h5 className="text-sm font-semibold text-purple-300 mb-2">例句：</h5>
                    <div className="space-y-3">
                      {pattern.examples.map((example, idx) => (
                        <div key={idx} className="bg-gray-600/30 p-3 rounded-lg">
                          <div className="text-sm mb-2">
                            <p className="text-blue-300">{example.en}</p>
                            <p className="text-gray-400">{example.zh}</p>
                          </div>
                          <AIDrillButton
                            example={example}
                            unitData={unitData}
                            userPreferences={userPreferences}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {pattern.phrasal_verbs && (
                  <div className="mt-3">
                    <h5 className="text-sm font-semibold text-purple-300 mb-2">片語動詞：</h5>
                    <div className="flex flex-wrap gap-2">
                      {pattern.phrasal_verbs.map((verb, idx) => (
                        <span key={idx} className="bg-blue-600/30 px-2 py-1 rounded text-sm">
                          {typeof verb === 'string' ? verb : verb.verb}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {pattern.linking_verbs && (
                  <div className="mt-3">
                    <h5 className="text-sm font-semibold text-purple-300 mb-2">連繫動詞分類：</h5>
                    <div className="space-y-2">
                      {Object.entries(pattern.linking_verbs).map(([category, verbs]) => (
                        <div key={category}>
                          <span className="text-yellow-300 text-sm">{category}:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {verbs.map((verb, idx) => (
                              <span key={idx} className="bg-green-600/30 px-2 py-1 rounded text-xs">
                                {verb}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {pattern.dative_verbs && (
                  <div className="mt-3">
                    <h5 className="text-sm font-semibold text-purple-300 mb-2">授與動詞分類：</h5>
                    <div className="space-y-2">
                      {Object.entries(pattern.dative_verbs).map(([prep, verbs]) => (
                        <div key={prep}>
                          <span className="text-yellow-300 text-sm">搭配 {prep}:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {verbs.map((verb, idx) => (
                              <span key={idx} className="bg-green-600/30 px-2 py-1 rounded text-xs">
                                {verb}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {pattern.verb_types && (
                  <div className="mt-3">
                    <h5 className="text-sm font-semibold text-purple-300 mb-2">動詞類型：</h5>
                    <div className="space-y-2">
                      {Object.entries(pattern.verb_types).map(([type, info]) => (
                        <div key={type} className="bg-gray-600/30 p-2 rounded">
                          <span className="text-yellow-300 text-sm font-semibold">{type}:</span>
                          <div className="mt-1">
                            <p className="text-sm text-gray-300">動詞: {info.verbs.join(', ')}</p>
                            <p className="text-sm text-gray-300">句型: {info.pattern}</p>
                            <p className="text-sm text-gray-400">{info.note}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderMemoryTips = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">記憶口訣與技巧</h3>
        
        <div className="space-y-4">
          <div>
            <h4 className="text-lg font-semibold mb-3 text-green-300">記憶口訣</h4>
            <div className="space-y-2">
              {content.memory_tips.tips.map((tip, index) => (
                <div key={index} className="bg-gray-700/50 p-3 rounded-lg">
                  <p className="text-gray-300">{tip}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3 text-green-300">學習步驟</h4>
            <div className="space-y-2">
              {content.memory_tips.learning_steps.map((step, index) => (
                <div key={index} className="bg-gray-700/50 p-3 rounded-lg">
                  <p className="text-gray-300">{step}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3 text-green-300">常見錯誤</h4>
            <div className="space-y-2">
              {content.memory_tips.common_mistakes.map((mistake, index) => (
                <div key={index} className="bg-red-600/20 p-3 rounded-lg border border-red-500/30">
                  <p className="text-gray-300">{mistake}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderQuizzes = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">練習題</h3>
        
        <div className="space-y-6">
          {content.quizzes.map((quiz, index) => (
            <div key={index} className="bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold mb-3 text-green-300">
                練習題 {index + 1}
              </h4>
              
              <div className="mb-4">
                <p className="text-gray-300 mb-3">{quiz.sentence}</p>
                <div className="space-y-2">
                  {Object.entries(quiz.options).map(([key, option]) => (
                    <div key={key} className="flex items-center">
                      <span className="text-yellow-300 font-mono mr-3">{key}.</span>
                      <span className="text-gray-300">{option}</span>
                    </div>
                  ))}
                </div>
              </div>

              {showAnswers && (
                <div className="bg-green-600/20 p-3 rounded-lg border border-green-500/30">
                  <p className="text-green-300 font-semibold">
                    正確答案: {quiz.options[Object.keys(quiz.options)[quiz.correct]]}
                  </p>
                  <p className="text-gray-300 mt-2">{quiz.explanation}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-6 flex gap-4">
          <button
            onClick={() => setShowAnswers(!showAnswers)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            {showAnswers ? '隱藏答案' : '顯示答案'}
          </button>
        </div>
      </div>
    </div>
  );

  const renderNavigation = () => (
    <div className="bg-gray-800/30 rounded-xl p-4 mb-6">
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setCurrentSection('overview')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'overview'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          句型概覽
        </button>
        <button
          onClick={() => setCurrentSection('memory_tips')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'memory_tips'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          記憶技巧
        </button>
        <button
          onClick={() => setCurrentSection('quizzes')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'quizzes'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          練習題
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-gray-800/30 rounded-xl p-6 mb-6">
          <h1 className="text-3xl font-bold mb-2 text-blue-300">
            {unitData.title[userPreferences?.language || 'traditional']}
          </h1>
          <p className="text-gray-300 mb-4">
            {unitData.subtitle[userPreferences?.language || 'traditional']}
          </p>
          <p className="text-gray-400">
            {unitData.description[userPreferences?.language || 'traditional']}
          </p>
        </div>

        {/* Navigation */}
        {renderNavigation()}

        {/* Content */}
        {currentSection === 'overview' && renderOverview()}
        {currentSection === 'memory_tips' && renderMemoryTips()}
        {currentSection === 'quizzes' && renderQuizzes()}

        {/* Complete Button */}
        <div className="mt-8 text-center">
          <button
            onClick={() => onMarkComplete && onMarkComplete(unitData.id)}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            標記為完成
          </button>
        </div>
      </div>
    </div>
  );
};

export default GrammarUnit1Content;
